/**
 * @module CanvasDetailPanel
 * @purpose Right-hand detail panel showing metadata tabs for the selected artefact.
 * @depends DecoratedArtefact (local types), DetailPanelTab (local types)
 * @dependedOnBy CanvasView
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: local tab selection only
 * - Errors: renders empty-state when artefact is null
 */

import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Bookmark } from 'lucide-react'
import type { DecoratedArtefact, DetailPanelTab } from './types'

const ACTIVE_TAB_CLASS =
  'px-3 py-1.5 text-xs font-[var(--font-mono)] uppercase tracking-wide border-b-2 border-[var(--accent)] text-[var(--accent)] transition-colors'

const INACTIVE_TAB_CLASS =
  'px-3 py-1.5 text-xs font-[var(--font-mono)] uppercase tracking-wide border-b-2 border-transparent transition-colors hover:text-[var(--accent)]'

const PANEL_WIDTH = '320px'

interface DetailPanelTabBarProps {
  activeTab: DetailPanelTab
  onTabChange: (tab: DetailPanelTab) => void
}

function DetailPanelTabBar({ activeTab, onTabChange }: DetailPanelTabBarProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  const tabDefs: Array<{ id: DetailPanelTab; label: string }> = [
    { id: 'overview', label: t('canvas.detailTabs.overview') },
    { id: 'metadata', label: t('canvas.detailTabs.metadata') },
    { id: 'raw', label: t('canvas.detailTabs.raw') },
    { id: 'related', label: t('canvas.detailTabs.related') },
  ]

  return (
    <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
      {tabDefs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => { onTabChange(tab.id) }}
          className={activeTab === tab.id ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS}
          style={{ color: activeTab === tab.id ? undefined : 'var(--text-muted)' }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

interface DetailTabContentProps {
  artefact: DecoratedArtefact
  activeTab: DetailPanelTab
  onBookmark: (id: string) => void
}

function DetailTabContent({
  artefact,
  activeTab,
  onBookmark,
}: DetailTabContentProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  if (activeTab === 'overview') {
    return (
      <div className="flex flex-col gap-3 p-3">
        <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
          {t('canvas.detail.overviewSummary')}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => { onBookmark(artefact.artefactId) }}
            title={t('canvas.bookmarkTransient')}
            className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-[var(--font-mono)] tracking-wide uppercase transition-colors hover:bg-[var(--accent-dim)]"
            style={{
              color: artefact.isBookmarked ? 'var(--accent)' : 'var(--text-muted)',
              border: '1px solid var(--border)',
            }}
          >
            <Bookmark className="h-3 w-3" />
            {t('canvas.detail.bookmark')}
          </button>
        </div>
        <div
          className="rounded p-2 text-xs"
          style={{ background: 'var(--accent-dim)', color: 'var(--text-dim)' }}
        >
          {t('canvas.detail.notes')}
        </div>
      </div>
    )
  }

  const stubMap: Record<DetailPanelTab, string> = {
    overview: '',
    metadata: t('canvas.detail.metadataStub'),
    raw: t('canvas.detail.rawStub'),
    related: t('canvas.detail.relatedStub'),
  }

  return (
    <div className="p-3">
      <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
        {stubMap[activeTab]}
      </p>
    </div>
  )
}

interface CanvasDetailPanelProps {
  artefact: DecoratedArtefact | null
  onBookmark: (id: string) => void
}

/**
 * Detail panel for a selected artefact.
 *
 * @behaviour Shows empty placeholder when no artefact selected.
 *            Tab bar switches between overview, metadata, raw, related views.
 * @errors n/a — purely display
 */
export function CanvasDetailPanel({
  artefact,
  onBookmark,
}: CanvasDetailPanelProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const [activeTab, setActiveTab] = useState<DetailPanelTab>('overview')

  const handleTabChange = useCallback((tab: DetailPanelTab) => {
    setActiveTab(tab)
  }, [])

  if (!artefact) {
    return (
      <div
        className="flex h-full items-center justify-center"
        style={{ width: PANEL_WIDTH, borderLeft: '1px solid var(--border)' }}
      >
        <p
          className="text-xs font-[var(--font-mono)] tracking-wide uppercase"
          style={{ color: 'var(--text-muted)' }}
        >
          {t('canvas.noSelection')}
        </p>
      </div>
    )
  }

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      style={{ width: PANEL_WIDTH, borderLeft: '1px solid var(--border)' }}
    >
      <div className="border-b px-3 py-2" style={{ borderColor: 'var(--border)' }}>
        <p className="truncate text-sm font-medium" style={{ color: 'var(--text)' }}>
          {artefact.displayName ?? artefact.artefactId}
        </p>
        <p className="text-xs font-[var(--font-mono)]" style={{ color: 'var(--text-muted)' }}>
          {artefact.artefactType}
        </p>
      </div>
      <DetailPanelTabBar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="flex-1 overflow-y-auto">
        <DetailTabContent artefact={artefact} activeTab={activeTab} onBookmark={onBookmark} />
      </div>
    </div>
  )
}
